/**
 * Me API — profile, password, sessions, file uploads.
 * All calls are scoped to the authenticated user's JWT.
 */
import { api } from './api';

// ─── Types ────────────────────────────────────────────────────

export interface ProfileData {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName: string | null;
  phone: string | null;
  phone2: string | null;
  avatarUrl: string | null;
  signatureUrl: string | null;
  jobTitle: string | null;
  memberType: string;
  twoFaEnabled: boolean;
  lastLoginAt: string | null;
  lastLoginIp: string | null;
  passwordChangedAt: string | null;
  createdAt: string;
  firstRunCompleted: boolean;
  chambers: {
    id: string; name: string; slug: string; logoUrl: string | null;
    plan: string; status: string; trialEndsAt: string | null;
  };
  barristerProfile: {
    id: string;
    bsbAuthNumber: string | null;
    callYear: number | null;
    inn: string | null;
    biography: string | null;
    shortBio: string | null;
    seniority: number;
    vatRegistered: boolean;
    vatNumber: string | null;
    practiceAreas: Array<{ id: string; name: string }>;
  } | null;
}

export interface UpdateProfileInput {
  firstName?: string;
  lastName?: string;
  displayName?: string;
  phone?: string;
  phone2?: string;
  jobTitle?: string;
  bsbAuthNumber?: string;
  callYear?: number;
  inn?: string;
  biography?: string;
  shortBio?: string;
}

export interface SessionInfo {
  id: string;
  userAgent: string | null;
  ip: string | null;
  createdAt: string;
  expiresAt: string;
  isCurrent: boolean;
}

// ─── Profile ──────────────────────────────────────────────────

export async function fetchMyProfile(): Promise<ProfileData> {
  const res = await api.get('/me/profile');
  return (res.data as any).data;
}

export async function updateMyProfile(data: UpdateProfileInput): Promise<ProfileData> {
  const res = await api.patch('/me/profile', data);
  return (res.data as any).data;
}

/** Mark the post-activation first-run experience complete (or skipped). */
export async function completeFirstRun(): Promise<void> {
  await api.post('/me/first-run/complete');
}

export interface OnboardingStatus {
  emailConnected: boolean;
  calendarConnected: boolean;
  firstMatterCreated: boolean;
  complete: boolean;
}

/** Live onboarding progress derived from real practice state. */
export async function fetchOnboardingStatus(): Promise<OnboardingStatus> {
  const res = await api.get('/me/onboarding-status');
  return (res.data as any).data;
}

// ─── Avatar ───────────────────────────────────────────────────

export async function uploadAvatar(file: File): Promise<{ avatarUrl: string }> {
  const form = new FormData();
  form.append('avatar', file);
  const res = await api.post('/me/avatar', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  return (res.data as any).data;
}

export async function deleteAvatar(): Promise<void> {
  await api.delete('/me/avatar');
}

// ─── Signature ────────────────────────────────────────────────

export async function uploadSignature(file: File): Promise<{ signatureUrl: string }> {
  const form = new FormData();
  form.append('signature', file);
  const res = await api.post('/me/signature', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  return (res.data as any).data;
}

export async function deleteSignature(): Promise<void> {
  await api.delete('/me/signature');
}

// ─── Password ─────────────────────────────────────────────────

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await api.patch('/me/password', { currentPassword, newPassword });
}

// ─── Sessions ─────────────────────────────────────────────────

export async function fetchSessions(): Promise<SessionInfo[]> {
  const res = await api.get('/me/sessions');
  return (res.data as any).data ?? [];
}

export async function revokeSession(id: string): Promise<void> {
  await api.delete(`/me/sessions/${id}`);
}

export async function revokeAllOtherSessions(): Promise<void> {
  await api.delete('/me/sessions/all-others');
}

// ─── Notification preferences ─────────────────────────────────
// The catalogue comes FROM THE SERVER with the values. React does not keep its own list of
// notification types: the page that did carried ten of the forty-three, so thirty-three could
// neither be seen nor switched off, and no toggle in Settings said so.

export type NotificationChannel = 'inApp' | 'push' | 'email';

export interface NotificationTypePref {
  type: string;
  category: string;
  channels: NotificationChannel[];
  /** Carries a professional or account obligation — offered, but not switchable. */
  mandatory: boolean;
  enabled: Record<NotificationChannel, boolean>;
}

export interface NotificationPreferences {
  categories: Array<{ id: string; label: string; description: string }>;
  types: NotificationTypePref[];
}

export async function fetchNotificationPreferences(): Promise<NotificationPreferences> {
  const res = await api.get('/me/notification-preferences');
  return (res.data as any).data;
}

/** Change one channel of one type. The server returns the whole resolved set back. */
export async function updateNotificationPreference(
  type: string,
  channel: NotificationChannel,
  enabled: boolean,
): Promise<NotificationPreferences> {
  const res = await api.patch('/me/notification-preferences', { [type]: { [channel]: enabled } });
  return (res.data as any).data;
}

// ─── Sign-in history ──────────────────────────────────────────

export interface LoginEvent {
  id: string;
  action: 'login' | 'failed_login' | 'logout';
  at: string;
  ip: string | null;
  userAgent: string | null;
  /** password | magic_link | google | recovery_code … null on older records. */
  method: string | null;
}

export interface LoginHistory {
  lastLoginAt: string | null;
  lastLoginIp: string | null;
  events: LoginEvent[];
}

export async function fetchLoginHistory(): Promise<LoginHistory> {
  const res = await api.get('/me/login-history');
  return (res.data as any).data;
}

// ─── Public Profile ───────────────────────────────────────────

export interface NotableCase {
  title: string;
  citation: string;
  year: number;
  summary: string;
}

export interface PublicProfileData {
  userId: string;
  firstName: string;
  lastName: string;
  displayName: string | null;
  email: string;
  phone: string | null;
  avatarUrl: string | null;
  memberType: string;
  chambers: { name: string; slug: string };
  barrister: {
    id: string;
    callYear: number | null;
    silkYear: number | null;
    inn: string | null;
    biography: string | null;
    shortBio: string | null;
    bsbAuthNumber: string | null;
    publicSlug: string | null;
    qualifications: string[];
    languages: string[];
    practiceAreas: Array<{ id: string; name: string; level: string }>;
    notableCases: NotableCase[];
    visibility: { winRate: boolean; matters: boolean; judgments: boolean };
  };
  stats: {
    activeMatters: number;
    winRate: number | null;
    judgments: number;
    closedMatters: number;
  };
}

export interface UpdatePublicProfileInput {
  biography?: string;
  shortBio?: string;
  inn?: string;
  callYear?: number;
  qualifications?: string[];
  languages?: string[];
  notableCases?: NotableCase[];
  publicShowWinRate?: boolean;
  publicShowMatters?: boolean;
  publicShowJudgments?: boolean;
}

// ─── Operational alerts ───────────────────────────────────────

export interface OperationalAlert {
  key: string;
  level: 'critical' | 'warning' | 'info';
  title: string;
  body: string;
  link: string;
  entityType: string;
  entityId: string;
}

export async function fetchOperationalAlerts(): Promise<OperationalAlert[]> {
  const res = await api.get('/me/operational-alerts');
  return (res.data as any).data ?? [];
}

// ─── Agenda — "Next 24 Hours" (server-composed timeline) ──────

export type AgendaItemType = 'hearing' | 'conference' | 'meeting' | 'task' | 'diary';

export interface AgendaItem {
  id: string;
  type: AgendaItemType;
  source: 'calendar' | 'task';
  title: string;
  startAt: string;
  endAt: string | null;
  timeLabel: string;               // "HH:MM" (London) or "All day" — computed server-side
  dayLabel: 'today' | 'tomorrow';  // computed server-side; the client never does date maths
  matterId: string | null;
  matterName: string | null;
  matterNumber: string | null;
  barristerName: string | null;    // event owner — shown on the clerk view
  location: string | null;
  status: string | null;
  priority: string | null;
  hearingId: string | null;
}

export async function fetchAgenda(): Promise<AgendaItem[]> {
  const res = await api.get('/me/agenda');
  return (res.data as any).data ?? [];
}

// ─── Diary item workspace (RC-DIARY Stage 1b) — lazy Tier-2 payload ─────
export interface DiaryItemDetail {
  id: string;
  caseId: string | null;
  hearingId: string | null;
  /** Set only when someone other than the diary owner booked it (e.g. a clerk). */
  scheduledBy: { name: string; role: string } | null;
  lifecycle: string;
  matter: {
    number: string;
    name: string;
    status: string;
    priority: string;
    client: string | null;
    solicitor: string | null;
    opponent: string | null;
    court: string | null;
    judge: string | null;
    courtroom: string | null;
    leadBarrister: string | null;
    juniorBarrister: string | null;
    clerk: string | null;
  } | null;
  hearing: { type: string; status: string } | null;
  readiness: {
    percentage: number;
    status: string;
    completed: string[];
    missing: string[];
    confidence?: string;
  } | null;
  nextAction: { label: string; type: string } | null;
  tasks: { open: number; overdue: number };
  documents: { count: number; recent: { id: string; name: string; category: string }[] };
  communications: { lastCommunicationAt: string | null; threadCount: number };
  money: {
    currency: string;
    billed: number;
    unbilled: number;
    collected: number;
    outstanding: number;
    billingRequired: boolean;
  } | null;
  timeline: { id: string; eventType: string; occurredAt: string }[];
  /** RC-STAGE2 — obligations with derived status (rendered in Stage 2C). */
  obligations: {
    id: string;
    type: string;
    title: string;
    status: 'resolved' | 'ready' | 'blocked';
    dueAt: string | null;
    blockedBy: { id: string; title: string }[];
  }[];
  /** RC-STAGE3 travel — arrive-by / leave-by for a hearing with a court (V1 fixed buffer). */
  travel: {
    court: { name: string | null; address: string | null; postcode: string | null; city: string | null };
    arriveBy: string;
    leaveBy: string;
    travelMinutes: number;
    arrivalOffsetMinutes: number;
    source: string;
  } | null;
}

export async function fetchDiaryItemDetail(eventId: string): Promise<DiaryItemDetail> {
  const res = await api.get(`/me/diary/${eventId}`);
  return (res.data as any).data;
}

// ─── Dashboard metrics (friction-reducing aggregation) ────────

export interface DashboardMetricsMatter {
  id: string;
  name: string;
  number?: string;
}

export interface OverdueTask {
  id: string;
  title: string;
  dueDate: string;
  priority: string;
  daysOverdue: number;
  matter: DashboardMetricsMatter | null;
}

export interface UpcomingDeadline {
  id: string;
  title: string;
  dueDate: string;
  priority: string;
  matter: { id: string; name: string } | null;
}

export interface UnpaidFeeNote {
  id: string;
  noteNumber: string;
  amount: number;
  status: string;
  dueDate: string | null;
  matter: DashboardMetricsMatter | null;
}

export interface DashboardMetrics {
  overdueTasks: {
    count: number;
    items: OverdueTask[];
  };
  upcomingDeadlines: {
    count: number;
    items: UpcomingDeadline[];
  };
  unpaidFees: {
    totalAmount: number;
    overdueAmount: number;
    count: number;
    overdueCount: number;
    items: UnpaidFeeNote[];
  };
  practiceHealth: {
    activeMatters: number;
    billedThisMonth: number;
    collectedThisMonth: number;
    invoicesThisMonth: number;
    winRate: number | null;
    closedMatters: number;
  };
  currency: string;
  asOf: string;
}

export async function fetchDashboardMetrics(): Promise<DashboardMetrics> {
  const res = await api.get('/me/dashboard-metrics');
  return (res.data as any).data;
}

// ─── Public profile ───────────────────────────────────────────

export async function fetchPublicProfile(): Promise<PublicProfileData> {
  const res = await api.get('/me/public-profile');
  return (res.data as any).data;
}

export async function updatePublicProfile(data: UpdatePublicProfileInput): Promise<PublicProfileData> {
  const res = await api.patch('/me/public-profile', data);
  return (res.data as any).data;
}

// ─── Chambers preferences ────────────────────────────────────

export interface ChambersPrefs {
  id: string;
  name: string;
  legalName: string | null;
  /** Confidential ORGANISATION fields — ABSENT from the payload for clerks and staff. */
  vatNumber?: string | null;
  registrationNo?: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  county: string | null;
  postcode: string | null;
  country: string;
  timezone: string;
  currency: string;
  locale: string;
  fiscalYearStart: string;
  logoUrl: string | null;
  invoiceHeaderType: string;
  invoiceLogoUrl: string | null;
  invoiceFullHeaderUrl: string | null;
  invoiceHeaderCustomText: string | null;
  invoiceFooterText: string | null;
  /** Server-resolved workspace shape. Never inferred client-side. */
  workspaceType: 'solo' | 'chambers';
  /** True when this caller owns the workspace (chambers admin, or solo-practice barrister). */
  isOwner: boolean;
  /** Whether the fields on this page are writable by the caller — the server decides, not the UI. */
  canEdit: boolean;
}

export async function fetchChambersPrefs(): Promise<ChambersPrefs> {
  const res = await api.get('/me/chambers');
  return (res.data as any).data;
}

export async function updateChambersPrefs(
  data: Partial<Omit<ChambersPrefs, 'id' | 'logoUrl' | 'country' | 'currency' | 'locale' | 'workspaceType' | 'isOwner' | 'canEdit'>>,
): Promise<ChambersPrefs> {
  const res = await api.patch('/me/chambers', data);
  return (res.data as any).data;
}

// ─── iCal subscription ───────────────────────────────────────

export interface IcalTokenData {
  token: string;
  url: string;
  /** ISO 8601 date-time string — the feed expires at this point and must be regenerated */
  expiresAt: string;
}

export async function fetchIcalToken(): Promise<IcalTokenData> {
  const res = await api.get<{ data: IcalTokenData }>('/calendar/ical-token');
  return res.data.data;
}

export async function regenerateIcalToken(): Promise<IcalTokenData> {
  const res = await api.post<{ data: IcalTokenData }>('/calendar/ical-token/regenerate');
  return res.data.data;
}

// ─── Sender profile (email identity) ─────────────────────────

export interface SenderProfileData {
  displayName: string | null;
  professionalEmail: string | null;
  clerkEmail: string | null;
  preferredSender: 'system' | 'professional' | 'clerk';
  replyTo: string | null;
}

export async function fetchSenderProfile(): Promise<SenderProfileData> {
  const res = await api.get<{ data: SenderProfileData }>('/me/sender-profile');
  return res.data.data;
}

export async function updateSenderProfile(data: Partial<SenderProfileData>): Promise<SenderProfileData> {
  const res = await api.put<{ data: SenderProfileData }>('/me/sender-profile', data);
  return res.data.data;
}

// ─── Personal preferences (PERSONAL scope) ───────────────────
// docs/SETTINGS-MODEL.md §4.4/§4.5. These follow the PERSON, not the browser — theme and
// density used to live in localStorage only, so a second machine lost them.

export interface UserPreferences {
  theme: 'light' | 'dark' | 'system';
  density: 'comfortable' | 'compact';
  sidebarCollapsed: boolean;
  /** null = inherit the chambers value. */
  timezone: string | null;
  locale: string | null;
  dateFormat: string;
  timeFormat: '24h' | '12h';
  landingPage: string | null;
  diaryDefaultView: string | null;
  diaryDefaultRange: string | null;
  workingDays: number[];
  workingHoursStart: string | null;
  workingHoursEnd: string | null;
  /** False when the user has never saved a preference — every value above is a default. */
  initialised: boolean;
  /** What actually applies once inheritance is resolved — computed on the server, never here. */
  effective: {
    timezone: string;
    locale: string;
    timezoneInherited: boolean;
    localeInherited: boolean;
  };
}

export async function fetchPreferences(): Promise<UserPreferences> {
  const res = await api.get('/me/preferences');
  return (res.data as any).data;
}

export async function updatePreferences(
  data: Partial<Omit<UserPreferences, 'effective'>>,
): Promise<UserPreferences> {
  const res = await api.patch('/me/preferences', data);
  return (res.data as any).data;
}

// ─── Fee settings (rate card) ─────────────────────────────────
// Same endpoint, response shape and React Query key (['me-fee-settings']) as the Fees & VAT
// settings page, so the two share one cached value. Barristers only — the API 404s for anyone
// without a barrister profile.

export interface MyFeeSettings {
  hourlyRate: number | null;
  briefFeeMin: number | null;
  refresherRate: number | null;
  vatRegistered: boolean;
  vatNumber: string | null;
  acceptCfa: boolean;
  acceptDba: boolean;
  acceptDirectAccess: boolean;
}

export async function fetchMyFeeSettings(): Promise<MyFeeSettings> {
  const res = await api.get('/me/fee-settings');
  return (res.data as any).data;
}
